import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import http from '../../../../shared/http'
import { formatVienna } from '../../../../shared/datetime'
import { stateFullLabel } from '../../../../shared/helpers'
import {
	JMC_CLASS_ROLE_LABELS,
	JMC_DOWNLOADS,
	JMC_GENDER_LABELS,
	JMC_GRADE_LABELS,
	JMC_LOG_LABELS,
	JMC_STATUS_LABELS
} from '../../../../shared/miniCompanies'

import styles from './parts.module.scss'

/*
 * Bausteine, die Lehrkraft und Verwaltung gleich zeigen (Anforderung #92):
 * Stand, Angaben, Kinderliste, Verlauf. Bearbeiten gibt es nur in der Ansicht
 * der Lehrkraft; hier wird nur angezeigt.
 */

export const personName = person => `${person?.firstName || ''} ${person?.lastName || ''}`.trim()

export const formatDay = value => (value ? formatVienna(value, 'dd.MM.yyyy') : '')

/** PDF über die angemeldete Schnittstelle holen und herunterladen. */
export const downloadPdf = (url, fileName) =>
	http.get(url, { responseType: 'blob' }).then(({ data }) => {
		const href = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
		const link = document.createElement('a')
		link.href = href
		link.setAttribute('download', fileName)
		document.body.appendChild(link)
		link.click()
		link.parentNode.removeChild(link)
		window.URL.revokeObjectURL(href)
	})

export function StatusPill({ status }) {
	const { t } = useTranslation()
	return (
		<span className={clsx(styles.status, styles[`status${status}`])} data-testid="jmc-status">
			{t(JMC_STATUS_LABELS[status] || status)}
		</span>
	)
}

export function DetailsList({ miniCompany }) {
	const { t } = useTranslation()
	return (
		<dl className={styles.details}>
			<dt>{t('Name of the Junior Mini Company')}</dt>
			<dd>{miniCompany.name}</dd>
			<dt>{t('Description of the business activity')}</dt>
			<dd className={styles.preline}>{miniCompany.businessDescription}</dd>
			<dt>{t('Year group of the pupils')}</dt>
			<dd>{t(JMC_GRADE_LABELS[miniCompany.grade] || miniCompany.grade)}</dd>
		</dl>
	)
}

/**
 * Die Schule der JMC mit ihren Stammdaten (Peter, 31.10.2026). Eine JMC hängt
 * fest an genau einer Schule — der der anlegenden Lehrkraft. Die Verwaltung
 * bekommt einen Verweis auf den Datensatz unter „Schulen“ (`schoolLink`).
 */
export function SchoolDetails({ school, state, schoolLink }) {
	const { t } = useTranslation()
	if (!school) return <p className={styles.muted}>—</p>
	const adresse = [[school.streetName, school.number].filter(Boolean).join(' '), [school.zipCode, school.location].filter(Boolean).join(' ')]
		.map(teil => String(teil || '').trim())
		.filter(Boolean)
		.join(', ')
	return (
		<dl className={styles.details}>
			<dt>{t('Name of the school')}</dt>
			<dd>{schoolLink ? <Link className={styles.link} to={schoolLink}>{school.name}</Link> : school.name}</dd>
			<dt>{t('School type')}</dt>
			<dd>{school.type || '—'}</dd>
			<dt>{t('Address')}</dt>
			<dd>{adresse || '—'}</dd>
			<dt>{t('Federal state')}</dt>
			<dd>{stateFullLabel(school.state || state) || '—'}</dd>
		</dl>
	)
}

export function TeacherList({ teachers, ownId, actions }) {
	const { t } = useTranslation()
	return (
		<ul className={styles.people}>
			{teachers.map(teacher => (
				<li key={teacher.id}>
					<div>
						<strong>
							{personName(teacher)} {teacher.id === ownId ? t('(you)') : ''}
						</strong>
						<span>{teacher.classRole ? t(JMC_CLASS_ROLE_LABELS[teacher.classRole]) : t('Role not set')}</span>
						{teacher.email && <span>{teacher.email}</span>}
					</div>
					{actions && actions(teacher)}
				</li>
			))}
		</ul>
	)
}

export function ParticipantsReadList({ participants, emptyText }) {
	const { t } = useTranslation()
	if (!participants.length) return <p className={styles.muted}>{emptyText || t('No entries yet.')}</p>
	return (
		<table className={styles.table}>
			<thead>
				<tr>
					<th>#</th>
					<th>{t('First name')}</th>
					<th>{t('Last name')}</th>
					<th>{t('Gender')}</th>
				</tr>
			</thead>
			<tbody>
				{participants.map((p, index) => (
					<tr key={p.id}>
						<td>{index + 1}</td>
						<td className={styles.strong}>{p.firstName}</td>
						<td className={styles.strong}>{p.lastName}</td>
						<td>{t(JMC_GENDER_LABELS[p.gender] || p.gender)}</td>
					</tr>
				))}
			</tbody>
		</table>
	)
}

export function LogList({ items }) {
	const { t } = useTranslation()
	if (!items?.length) return <p className={styles.muted}>{t('No entries yet.')}</p>
	return (
		<ol className={styles.log}>
			{items.map(entry => (
				<li key={entry.id}>
					<span className={styles.logWhen}>{formatVienna(entry.createdAt, 'dd.MM.yyyy HH:mm')}</span>
					<span className={styles.logWhat}>
						<strong>{t(JMC_LOG_LABELS[entry.eventKey] || entry.eventKey)}</strong>
						{entry.message ? <span className={styles.preline}> — {entry.message}</span> : null}
						{entry.author?.name ? <span className={styles.logWho}> · {entry.author.name}</span> : null}
					</span>
				</li>
			))}
		</ol>
	)
}

/** Die Unterlagen von JAA zum Herunterladen — Schritt 3, für Lehrkraft und Verwaltung gleich. */
export function DownloadsList() {
	const { t } = useTranslation()
	return (
		<ul className={styles.downloads}>
			{JMC_DOWNLOADS.map(d => (
				<li key={d.file}>
					<div>
						<strong>{t(d.title)}</strong>
						<span>{t(d.text)}</span>
					</div>
					<a className={styles.downloadLink} href={d.file} target="_blank" rel="noopener noreferrer" download>
						{t('Download')}
					</a>
				</li>
			))}
		</ul>
	)
}

/** Die Vorlage der Einverständniserklärung — auch direkt beim Hochladen (Schritt 4). */
export const CONSENT_TEMPLATE = JMC_DOWNLOADS[0]
